import React, { useEffect, useState } from 'react'
import { TemplateFormContainer } from './style'
import { TaskElementProps } from './Actions'
import { CampaignTaskElements } from 'services/api/campaignTemplates'
import TemplateTask from './TemplateTask'
import { useFormikContext } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { TemplateDetailSchema } from '../TemplateDetail'
import OnFormikValueChange from '../OnFormikValueChange'
import { updateCampaignTaskElements, CampaignTemplateStep } from 'state/campaignTemplates'
import { useNewTranslation } from 'hooks'
import { ElementKeys, fieldKeyByTasks } from '../TemplateDetail/options'
import {
    updateReviewTextImageStep1Data,
    updateReviewTextImageInterpolationListData,
} from 'state/appPreview/action/reviewTextImage'
import { formatTaskValues } from 'components/utils/interpolation'
import { selectPurchaseFirstStepData } from 'state/appPreview/selector/purchase'
import { selectTemplateFirstStepDataByTaskIndex } from 'state/appPreview/selector'
import { selectCampaignByTemplateData } from 'state'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import PlaceholderComponent from 'components/AppPreview/common/InterpolationList'

const identifiersMap: Partial<Record<ElementKeys, string>> = {
    review_text: CampaignTemplateStep['REVIEW_TEXT_NO_APPROVAL'],
    review_text_approval: CampaignTemplateStep['REVIEW_TEXT_APPROVAL'],
    review_text_image_approval: CampaignTemplateStep['REVIEW_TEXT_IMAGE_APPROVAL'],
    review_text_image_approval_purchase: CampaignTemplateStep['REVIEW_TEXT_IMAGE_APPROVAL_PURCHASE'],
    review_text_purchase: CampaignTemplateStep['REVIEW_TEXT_NO_APPROVAL_PURCHASE'],
}

const ReviewTask = ({
    showError: baseShowError,
    taskOrder,
    isReadOnly,
    campaignTemplateStepKey,
    ...props
}: TaskElementProps) => {
    const fieldNames = fieldKeyByTasks[campaignTemplateStepKey]

    const [t] = useNewTranslation()
    const { values } = useFormikContext<TemplateDetailSchema>()
    const [showError, setShowError] = useState(baseShowError)
    const dispatch = useDispatch()
    const campaign = useSelector(selectCampaignByTemplateData)
    const selectedTemplateFirstStepData = useSelector(selectTemplateFirstStepDataByTaskIndex(taskOrder))
    const purchaseFirstStepData = useSelector(selectPurchaseFirstStepData)
    const itemsToReviewFromDetail = values?.task_elements?.[taskOrder].items_to_review
    const { itemsToReview: itemsToReviewFromPreview } = purchaseFirstStepData
    const itemToReview = itemsToReviewFromPreview ? itemsToReviewFromPreview : itemsToReviewFromDetail
    const namePrefix = `task_elements.${taskOrder}.`
    const validateFieldsKey = fieldNames.map(field => namePrefix + field) as (keyof TemplateDetailSchema)[]
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE
    const wrongFormatCopy = t('campaign.template.downloadAppFrom.errorMessage.wrongFormate')

    useEffect(() => {
        handleDebouncedChange(values)
    }, [])

    // prepare step details based on various review type
    const getStepInformation = (): {
        thingsToNote?: string
        requirements?: string
        textSubmission?: string
        maxPhotoSubmission?: number
        requirementTitle?: string
    } => {
        if (campaignTemplateStepKey === 'review_text' || campaignTemplateStepKey === 'review_text_purchase') {
            return {
                thingsToNote: selectedTemplateFirstStepData.step?.blocks?.[0].description ?? '',
                requirements: selectedTemplateFirstStepData.step?.blocks?.[1].description ?? '',
                requirementTitle: selectedTemplateFirstStepData.step?.blocks?.[1].title ?? '',
                maxPhotoSubmission: selectedTemplateFirstStepData.step?.blocks?.[2].max_photos,
            }
        }

        if (campaignTemplateStepKey === 'review_text_approval') {
            return {
                requirements: selectedTemplateFirstStepData.step?.blocks?.[0].description ?? '',
                requirementTitle: selectedTemplateFirstStepData.step?.blocks?.[0].title ?? '',
                textSubmission: selectedTemplateFirstStepData.step?.blocks?.[1].title ?? '',
            }
        }

        if (
            campaignTemplateStepKey === 'review_text_image_approval' ||
            campaignTemplateStepKey === 'review_text_image_approval_purchase'
        ) {
            return {
                requirements: selectedTemplateFirstStepData.step?.blocks?.[0].description ?? '',
                requirementTitle: selectedTemplateFirstStepData.step?.blocks?.[0].title ?? '',
                textSubmission: selectedTemplateFirstStepData.step?.blocks?.[2].title ?? '',
                maxPhotoSubmission: selectedTemplateFirstStepData.step?.blocks?.[1].max_photos,
            }
        }

        return {}
    }

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive) {
            return false
        }

        return isReadOnly
    }

    const onSaveData = (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => {
        const validTemplateDataUuid = typeof props.templateUuid === 'string' ? props.templateUuid : ''
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        dispatch(
            updateCampaignTaskElements({
                templateUuid: validTemplateDataUuid,
                ...taskValue,
                setNavigationDisable: props.setNavigationDisable,
                showSuccessToast,
            }),
        )
    }

    const handleDebouncedChange = (fieldsValues: Partial<TemplateDetailSchema>) => {
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        if (taskValue.element) {
            const taskTitle = selectedTemplateFirstStepData?.taskTitle ?? ''
            const { requirements, textSubmission, thingsToNote, maxPhotoSubmission, requirementTitle } =
                getStepInformation()

            const textBlockTitle = textSubmission ? (
                <PlaceholderComponent
                    template={textSubmission}
                    values={[
                        {
                            emptyValue: t('campaignCategory.socialMediaPlatformTitle'),
                            review_platform: taskValue.review_platform,
                            isInline: true,
                        },
                    ]}
                />
            ) : undefined

            const uniqueSellingPoints = formatTaskValues(taskValue?.unique_selling_point, {
                isList: true,
                bulletPoint: '\u002D',
            })

            const payload = {
                thingsToNote,
                taskTitle,
                textBlockTitle,
                title: requirementTitle,
                mediaBlockMaxPhotos: maxPhotoSubmission,
                // review_platform is for interpolating `review_platform` in the Campaign description,
                // task title/description, and step title in Campaign detail preview.
                review_platform: taskValue.review_platform,
            }

            const interpolationListPayload = {
                description: {
                    template: requirements ?? '',
                    values: [
                        {
                            unique_selling_point: uniqueSellingPoints,
                            emptyValue: '\u002D ' + t('campaign.template.keyPoints.appPreview.placeholder'),
                        },
                        {
                            review_link: taskValue?.review_link,
                            emptyValue: t('campaign.template.link.appPreview.placeholder'),
                            isInline: true,
                            isUrl: true,
                        },
                        {
                            items_to_review: itemToReview?.join(', '),
                            emptyValue: t('campaign.template.itemsToReview.appPreview.placeholder'),
                            isInline: true,
                        },
                        {
                            brand_name: taskValue.brand_name,
                            emptyValue: t('manageTeams.teamPage.brandNameTableLabel'),
                            isInline: true,
                        },
                    ],
                },
                stepTitle: {
                    template: selectedTemplateFirstStepData.step?.title ?? '',
                    values: [
                        {
                            review_platform: taskValue.review_platform,
                            emptyValue: t('campaignCategory.socialMediaPlatformTitle'),
                            isInline: true,
                        },
                    ],
                },
            }

            dispatch(updateReviewTextImageStep1Data(payload))
            dispatch(updateReviewTextImageInterpolationListData(interpolationListPayload))

            if (!isCampaignLive && !isReadOnly) {
                onSaveData(fieldsValues, false)
            }
        }
    }

    const onShowError = () => {
        setShowError(true)
    }

    return (
        <TemplateFormContainer data-testid="test-review-text-task-page">
            <OnFormikValueChange
                callback={handleDebouncedChange}
                fields={validateFieldsKey}
                skipCallbackError={{
                    [namePrefix + 'review_link']: [wrongFormatCopy],
                }}
                validateSpecificField={props.validateSpecificField}
                showError={showError}
                updateCurrentStepStatus={props.updateCurrentStepStatus}
                // Only disableNavigation for input if campaign is not live
                {...(!isCampaignLive && { setDisableNavigation: props.setNavigationDisable })}
                disableAutoSave={disableAutoSave()}
            />

            <TemplateTask
                elementKey={campaignTemplateStepKey}
                field_keys={fieldNames}
                taskOrder={taskOrder}
                showError={showError}
                identifierPrefix={
                    identifiersMap[campaignTemplateStepKey] ?? CampaignTemplateStep['REVIEW_TEXT_APPROVAL']
                }
                campaignTemplateStepKey={campaignTemplateStepKey}
                onSaveData={onSaveData}
                onShowError={onShowError}
                fields={validateFieldsKey}
                {...props}
            />
        </TemplateFormContainer>
    )
}

export default ReviewTask
